import {
  Box,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { shallow } from 'zustand/shallow';

import { Button, Text } from '@revelio/ui';

import type { RowLimitResultsByInstanceId } from '../api/data-builder-row-limit.types';
import { useDataBuilderQuery } from '../api/useDataBuilderQuery';
import { DatasetSwitcherItem } from '../components/ui/DatasetSwitcherItem';
import styles from '../components/ui/dataset-switcher.module.css';
import {
  selectActiveInstanceId,
  selectConfigByInstance,
  selectDatasetInstances,
  selectInstanceIdsAwaitingCompanyMapper,
  selectModelVersions,
  useDataBuilderActions,
  useDataBuilderSelector,
} from '../data-builder.store';
import {
  buildDatasetInstanceLabelMap,
  buildDatasetLabelMap,
} from '../datasets/dataset-instance-labels';
import { getDeliverableColumnConfig } from '../datasets/deliverable-column-config';
import { seedInstanceDefaults } from '../datasets/initial-instance-config';
import {
  applyInheritedUoa,
  pickLastUsedUoaForTarget,
} from '../unit-of-analysis-defaulting';
import {
  buildConditionContext,
  filterUnitOptionsByModels,
} from './step2-column/filter-config-by-models';

type Step2DatasetSwitcherProps = {
  instanceIdsWithErrors: Set<string>;
  showValidationErrors: boolean;
  rowLimitResultsByInstanceId: RowLimitResultsByInstanceId;
  pendingInstanceIds: Set<string>;
  apiErrorInstanceIds: Set<string>;
  isWaitingForCompanies: boolean;
};

export const Step2DatasetSwitcher = ({
  instanceIdsWithErrors,
  showValidationErrors,
  rowLimitResultsByInstanceId,
  pendingInstanceIds,
  apiErrorInstanceIds,
  isWaitingForCompanies,
}: Step2DatasetSwitcherProps) => {
  const { data } = useDataBuilderQuery();
  const instances = useDataBuilderSelector(selectDatasetInstances);
  const activeInstanceId = useDataBuilderSelector(selectActiveInstanceId);
  const configByInstance = useDataBuilderSelector(selectConfigByInstance);
  const modelVersions = useDataBuilderSelector(selectModelVersions);
  const awaitingMapperInstanceIds = useDataBuilderSelector(
    selectInstanceIdsAwaitingCompanyMapper,
    shallow
  );
  const {
    setActiveInstanceId,
    addDatasetInstance,
    removeDatasetInstance,
    renameDatasetInstance,
    setUnitOfAnalysis,
    setUnitOfAnalysisSelections,
    setCompanySelections,
    setCompanySets,
    setCompanyMapper,
  } = useDataBuilderActions();
  const { isOpen, onToggle, onClose } = useDisclosure();
  const lastAutoActivatedIdRef = useRef<string | null>(null);

  useEffect(() => {
    const firstId = instances[0]?.instanceId;
    if (activeInstanceId !== null || firstId === undefined) return;
    if (lastAutoActivatedIdRef.current === firstId) return;
    lastAutoActivatedIdRef.current = firstId;
    setActiveInstanceId(firstId);
  }, [activeInstanceId, instances, setActiveInstanceId]);

  const labelMap = buildDatasetLabelMap(data?.datasets);
  const instanceLabels = buildDatasetInstanceLabelMap(instances, labelMap);
  const items = instances.map((inst) => ({
    id: inst.instanceId,
    label: instanceLabels[inst.instanceId] ?? inst.datasetKey,
  }));

  if (items.length === 0) {
    return null;
  }

  const handleAddDataset = (datasetKey: string) => {
    const existingInstanceIds = instances.map((inst) => inst.instanceId);
    const deliverableConfig = getDeliverableColumnConfig(
      data?.deliverablesConfig ?? {},
      datasetKey
    );
    const defaultSettings = Object.fromEntries(
      (deliverableConfig?.settings ?? []).map((setting) => [
        setting.key,
        setting.default,
      ])
    );
    const conditionContext = buildConditionContext(
      modelVersions,
      null,
      defaultSettings
    );
    // Seed file/column defaults at instance-creation time so the duplicated
    // dataset doesn't have to wait on a Step 2 effect to populate them. Pass the
    // condition context so condition-gated defaults aren't seeded when the
    // initial settings can't satisfy them (e.g. unified-only columns for a
    // client without unified posting-source access).
    const initialConfig =
      deliverableConfig !== undefined
        ? {
            ...seedInstanceDefaults(deliverableConfig, conditionContext),
            settings: defaultSettings,
          }
        : undefined;
    const newId = addDatasetInstance(datasetKey, initialConfig);

    const availableValues = filterUnitOptionsByModels(
      deliverableConfig?.unitOfAnalysis.options ?? [],
      conditionContext
    ).map((option) => option.value);
    const pickedUoa = pickLastUsedUoaForTarget({
      configByInstance,
      orderedInstanceIds: existingInstanceIds,
      availableUoaValues: availableValues,
    });
    if (pickedUoa !== null) {
      applyInheritedUoa(
        {
          setUnitOfAnalysis,
          setUnitOfAnalysisSelections,
          setCompanySelections,
          setCompanySets,
          setCompanyMapper,
        },
        newId,
        pickedUoa,
        configByInstance[pickedUoa.sourceInstanceId]
      );
    }

    setActiveInstanceId(newId);
    onClose();
  };

  const availableDatasets = (data?.datasets ?? []).filter(
    (ds) => !ds.isDisabled
  );

  return (
    <Box
      data-testid="data-builder-dataset-switcher"
      className={styles.switcherRail}
      minWidth="280px"
      maxWidth="320px"
      flexShrink={0}
      height="100%"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <Box flex={1} minHeight={0} overflow="auto">
        <Box className={styles.itemsGroup}>
          {items.map((item) => (
            <DatasetSwitcherItem
              key={item.id}
              id={item.id}
              label={item.label}
              isActive={item.id === activeInstanceId}
              hasError={
                showValidationErrors && instanceIdsWithErrors.has(item.id)
              }
              rowLimitInfo={rowLimitResultsByInstanceId[item.id]}
              rowLimitLoading={
                pendingInstanceIds.has(item.id) ||
                // The row count is about to be re-probed with the new mapper,
                // so the dataset is loading while its mapper generates too.
                awaitingMapperInstanceIds.includes(item.id) ||
                (isWaitingForCompanies &&
                  rowLimitResultsByInstanceId[item.id] === undefined)
              }
              rowLimitApiError={apiErrorInstanceIds.has(item.id)}
              onClick={() => setActiveInstanceId(item.id)}
              onRename={(name) => renameDatasetInstance(item.id, name)}
              onDelete={() => removeDatasetInstance(item.id)}
            />
          ))}
        </Box>

        <Box padding="2">
          <Popover placement="bottom" isOpen={isOpen} onClose={onClose}>
            <PopoverTrigger>
              <div>
                <Button
                  data-testid="data-builder-add-dataset"
                  className={styles.addDatasetButton}
                  color="lightBlue"
                  variant="ghost"
                  size="xs"
                  onClick={onToggle}
                  fullWidth
                >
                  + Add Dataset
                </Button>
              </div>
            </PopoverTrigger>
            <PopoverContent width="240px" className={styles.addDatasetPopover}>
              <PopoverBody padding={0} maxHeight="280px" overflow="auto">
                {availableDatasets.map((ds) => (
                  <Flex
                    key={ds.id}
                    data-testid={`data-builder-add-dataset-option-${ds.id}`}
                    className={styles.addDatasetOption}
                    padding="8px 12px"
                    cursor="pointer"
                    onClick={() => handleAddDataset(ds.id)}
                  >
                    <Text fontSize="sm">{ds.label}</Text>
                  </Flex>
                ))}
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </Box>
      </Box>
    </Box>
  );
};
